import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, 
  Alert, CircularProgress, Stack, 
  Accordion, AccordionSummary, AccordionDetails, Divider
} from '@mui/material';
import { 
  Science as ScienceIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { getSharedWaxTests, getWaxTest } from '../api';

export default function SharedWaxTestsSection({ team }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTests = useCallback(async () => {
    if (!team?.id) return;
    try {
      setLoading(true);
      const data = await getSharedWaxTests(team.id);
      setTests(data);
      setError(null);
    } catch (err) {
      setError('Failed to load shared wax tests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [team?.id]);

  useEffect(() => {
    if (team?.id) {
      loadTests();
    }
  }, [team?.id, loadTests]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'setup': return 'default';
      case 'skis_added': return 'primary';
      case 'ranking': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getTestTypeColor = (testType) => {
    switch (testType) {
      case 'grip': return 'error';
      case 'glide': return 'info';
      default: return 'default';
    }
  };

  const getTechniqueColor = (technique) => {
    switch (technique) {
      case 'classic': return 'success';
      case 'skate': return 'primary';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'setup': return 'Setup';
      case 'skis_added': return 'Skis Added';
      case 'ranking': return 'Ranking';
      case 'completed': return 'Completed';
      default: return status;
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Shared Wax Tests
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {tests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ScienceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No shared wax tests yet
          </Typography>
          <Typography color="text.secondary">
            Coaches can share their wax test results with this team.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {tests.map((test) => (
            <Paper key={test.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {test.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {new Date(test.date).toLocaleDateString()} • Shared by {test.user_name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={test.technique}
                    color={getTechniqueColor(test.technique)}
                    size="small"
                  />
                  <Chip
                    label={test.test_type}
                    color={getTestTypeColor(test.test_type)}
                    size="small"
                  />
                  <Chip
                    label={getStatusText(test.status)}
                    color={getStatusColor(test.status)}
                    size="small"
                  />
                </Box>
              </Box>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">View Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>Conditions</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Location:</strong> {test.location || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Temperature:</strong> {test.temperature ? `${test.temperature}°C` : 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Humidity:</strong> {test.humidity ? `${test.humidity}%` : 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Snow Conditions:</strong> {test.snow_conditions || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Snow Type:</strong> {test.snow_type || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Air Conditions:</strong> {test.air_conditions || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>Test Info</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Skis:</strong> {test.ski_count || 0}
                        </Typography>
                        {test.status === 'completed' && (
                          <Typography variant="body2">
                            <strong>Ranked:</strong> {test.ranked_skis || 0}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          <strong>Method:</strong> {test.ranking_method || 'Not set'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Shared:</strong> {new Date(test.shared_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>Ranking Results</Typography>
                      <Box sx={{ mb: 2 }}>
                        {test.status === 'completed' && test.ranked_skis > 0 ? (
                          <RankingDiagram test={test} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {test.status === 'completed' ? 'No ranking data available' : 'Test not completed yet'}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    {test.notes && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                        <Typography variant="body2">{test.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}

// Ranking Diagram Component
function RankingDiagram({ test }) {
  const [skis, setSkis] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTestSkis = useCallback(async () => {
    try {
      const testData = await getWaxTest(test.id);
      setSkis(testData.skis || []);
    } catch (err) {
      console.error('Failed to load test skis:', err);
    } finally {
      setLoading(false);
    }
  }, [test.id]);

  useEffect(() => {
    loadTestSkis();
  }, [loadTestSkis]);

  if (loading) {
    return <CircularProgress size={20} />;
  }

  const rankedSkis = skis
    .filter(ski => ski.rank !== null)
    .sort((a, b) => a.rank - b.rank);

  if (rankedSkis.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No ranking data available
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {rankedSkis.map((ski, index) => (
          <React.Fragment key={ski.id}>
            {/* Ski Ranking Item */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: index === 0 ? 'success.light' : 'background.paper',
              border: index === 0 ? '2px solid' : '1px solid',
              borderColor: index === 0 ? 'success.main' : 'divider',
              minWidth: 80,
              position: 'relative'
            }}>
              {/* Winner Icon */}
              {index === 0 && (
                <Typography variant="h6" sx={{ position: 'absolute', top: -8, right: -8 }}>
                  🏆
                </Typography>
              )}

              {/* Rank Badge */}
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: index === 0 ? 'success.main' : 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}>
                {ski.rank}
              </Box>
              
              {/* Ski Info */}
              <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {ski.ski_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontSize: '0.7rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  maxWidth: 80
                }}>
                  {ski.wax_description}
                </Typography>
              </Box>
            </Box>

            {/* Distance Arrow */}
            {index < rankedSkis.length - 1 && ski.distance_behind_previous && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 60
              }}>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  textAlign: 'center'
                }}>
                  {ski.distance_behind_previous}cm
                </Typography>
                <Box sx={{ 
                  width: 30, 
                  height: 2, 
                  backgroundColor: 'divider',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: -4,
                    top: -3,
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid',
                    borderLeftColor: 'divider',
                    borderTop: '4px solid transparent',
                    borderBottom: '4px solid transparent'
                  }
                }} />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
